'use client';

/**
 * GDPR/ePrivacy consent banner: non-essential cookies (PostHog) must not load
 * until the user grants explicit consent. Essential cookies cannot be disabled.
 *
 * Forced light theme (the `light` class re-applies light tokens regardless of
 * an ancestor `.dark`) because it only appears on the always-light marketing
 * surface.
 */

import Link from 'next/link';
import { useCookieConsent } from '@/components/providers/cookie-consent-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useHydrated } from '@/hooks/use-hydrated';

export function CookieConsentBanner() {
  const { hasConsented, isAppSession, acceptAll, rejectAll } =
    useCookieConsent();
  const mounted = useHydrated();

  // Inside the authenticated app, analytics is covered by the account
  // agreement, so the banner is not shown.
  if (!mounted || hasConsented || isAppSession) {
    return null;
  }

  return (
    <Card
      role='dialog'
      aria-label='Cookie consent'
      className='light motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 fixed inset-x-4 bottom-4 z-50 mx-auto max-w-2xl motion-reduce:animate-none'
    >
      <CardContent className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex-1 space-y-1'>
          <p className='text-sm font-medium'>We value your privacy</p>
          <p className='text-muted-foreground text-sm'>
            We use essential cookies for core functionality (always on) and
            optional analytics cookies to improve our platform. You can accept
            all, or keep only the necessary ones.{' '}
            <Link
              href='/privacy'
              prefetch={false}
              className='hover:text-foreground underline underline-offset-4'
            >
              Privacy Policy
            </Link>
          </p>
        </div>
        <div className='flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center'>
          <Button
            variant='outline'
            size='sm'
            onClick={rejectAll}
            className='w-full sm:w-auto'
          >
            Only necessary
          </Button>
          <Button size='sm' onClick={acceptAll} className='w-full sm:w-auto'>
            Accept all
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
